import type { EnvelopeV1, PassphraseWrapper, PrfWrapper, Wrapper } from '../../shared/types';

/**
 * The privacy boundary. Everything here runs in the browser with WebCrypto.
 * Addresses are AES-256-GCM encrypted under a random DEK; the DEK is wrapped
 * once per unlock method: a passkey's PRF output (HKDF → KEK) or a passphrase
 * (PBKDF2 → KEK). The server only ever sees the sealed envelope.
 */
export type WalletEntry = { label: string; address: string };
export type VaultPayload = { addresses: WalletEntry[] };

export const PBKDF2_ITERATIONS = 600_000;
const KEK_INFO = 'canary-kek-v1';
const enc = new TextEncoder();
const dec = new TextDecoder();

export function toB64u(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}

export function fromB64u(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b = atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const out = new Uint8Array(b.length);
  for (let i = 0; i < b.length; i++) out[i] = b.charCodeAt(i);
  return out;
}

const randomBytes = (n: number) => crypto.getRandomValues(new Uint8Array(n));

export function generateDek(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

function importAes(raw: BufferSource, extractable: boolean): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, 'AES-GCM', extractable, ['encrypt', 'decrypt']);
}

export async function kekFromPrf(prf: BufferSource, salt: Uint8Array): Promise<CryptoKey> {
  const ikm = await crypto.subtle.importKey('raw', prf, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt, info: enc.encode(KEK_INFO) }, ikm, 256);
  return importAes(bits, false);
}

export async function kekFromPassphrase(pass: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const pw = await crypto.subtle.importKey('raw', enc.encode(pass.normalize('NFKC')), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, pw, 256);
  return importAes(bits, false);
}

async function aesEncrypt(key: CryptoKey, plaintext: Uint8Array): Promise<{ iv: string; data: string }> {
  const iv = randomBytes(12);
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext));
  return { iv: toB64u(iv), data: toB64u(ct) };
}

async function aesDecrypt(key: CryptoKey, iv: string, data: string): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromB64u(iv) }, key, fromB64u(data)));
}

async function wrapDek(dek: CryptoKey, kek: CryptoKey): Promise<{ iv: string; wrappedDek: string }> {
  const raw = new Uint8Array(await crypto.subtle.exportKey('raw', dek));
  const { iv, data } = await aesEncrypt(kek, raw);
  return { iv, wrappedDek: data };
}

async function unwrapDek(w: { iv: string; wrappedDek: string }, kek: CryptoKey): Promise<CryptoKey> {
  return importAes(await aesDecrypt(kek, w.iv, w.wrappedDek), true);
}

export async function makePrfWrapper(dek: CryptoKey, credId: string, prf: BufferSource): Promise<PrfWrapper> {
  const salt = randomBytes(32);
  return { kind: 'prf', credId, salt: toB64u(salt), ...(await wrapDek(dek, await kekFromPrf(prf, salt))) };
}

export async function makePassphraseWrapper(dek: CryptoKey, pass: string, iterations = PBKDF2_ITERATIONS): Promise<PassphraseWrapper> {
  const salt = randomBytes(32);
  return { kind: 'passphrase', salt: toB64u(salt), iterations, ...(await wrapDek(dek, await kekFromPassphrase(pass, salt, iterations))) };
}

export async function sealEnvelope(payload: VaultPayload, dek: CryptoKey, wrapped: Wrapper[], updatedAt = 0): Promise<EnvelopeV1> {
  const { iv, data } = await aesEncrypt(dek, enc.encode(JSON.stringify(payload)));
  return { v: 1, iv, ciphertext: data, wrapped, updatedAt };
}

export async function openEnvelope(env: EnvelopeV1, dek: CryptoKey): Promise<VaultPayload> {
  const payload = JSON.parse(dec.decode(await aesDecrypt(dek, env.iv, env.ciphertext))) as VaultPayload;
  if (!Array.isArray(payload?.addresses)) throw new Error('corrupt envelope');
  return payload;
}

export const prfWrapperFor = (env: EnvelopeV1, credId: string): PrfWrapper | undefined =>
  env.wrapped.find((w): w is PrfWrapper => w.kind === 'prf' && w.credId === credId);
export const hasPassphrase = (env: EnvelopeV1) => env.wrapped.some((w) => w.kind === 'passphrase');

export async function unlockWithPrf(env: EnvelopeV1, credId: string, prf: BufferSource): Promise<CryptoKey> {
  const w = prfWrapperFor(env, credId);
  if (!w) throw new Error('This passkey holds no key for the vault. Unlock with your passphrase, then add this passkey in Settings.');
  try {
    return await unwrapDek(w, await kekFromPrf(prf, fromB64u(w.salt)));
  } catch {
    throw new Error('Passkey secret did not open the vault.');
  }
}

export async function unlockWithPassphrase(env: EnvelopeV1, pass: string): Promise<CryptoKey> {
  const w = env.wrapped.find((x): x is PassphraseWrapper => x.kind === 'passphrase');
  if (!w) throw new Error('No passphrase is set for this vault.');
  try {
    return await unwrapDek(w, await kekFromPassphrase(pass, fromB64u(w.salt), w.iterations));
  } catch {
    throw new Error('Wrong passphrase.');
  }
}

/** Add or replace a wrapper (one passphrase; one PRF wrapper per passkey). */
export function withWrapper(env: EnvelopeV1, w: Wrapper): EnvelopeV1 {
  const keep = env.wrapped.filter((x) => (x.kind !== w.kind ? true : x.kind === 'prf' && w.kind === 'prf' && x.credId !== w.credId));
  return { ...env, wrapped: [...keep, w] };
}
